import { request, unwrap } from '../client';
import type {
  AddMemberRequest,
  ApiResponse,
  MemberKind,
  TenantMemberDto,
  TenantUserRole,
  TenantUserStatus,
} from '../types';

export const membersApi = {
  async list(tenantId: number): Promise<TenantMemberDto[]> {
    const res = await request<ApiResponse<TenantMemberDto[]>>(
      'GET',
      `/api/saas-admin/tenants/${tenantId}/members`,
    );
    return unwrap(res);
  },

  async add(tenantId: number, req: AddMemberRequest): Promise<TenantMemberDto> {
    const res = await request<ApiResponse<TenantMemberDto>>(
      'POST',
      `/api/saas-admin/tenants/${tenantId}/members`,
      { json: req },
    );
    return unwrap(res);
  },

  // SASADM-0169 (saas-auth SASATH-0376) — role/status/remove carry `?kind=CUSTOMER|STAFF`:
  // a membership id can exist in both tables, and an unqualified call is `409 MEMBER_ID_AMBIGUOUS`.
  async setRole(
    tenantId: number,
    membershipId: number,
    role: TenantUserRole,
    kind: MemberKind,
  ): Promise<TenantMemberDto> {
    const res = await request<ApiResponse<TenantMemberDto>>(
      'PUT',
      `/api/saas-admin/tenants/${tenantId}/members/${membershipId}/role`,
      { json: { role }, query: { kind } },
    );
    return unwrap(res);
  },

  async setStatus(
    tenantId: number,
    membershipId: number,
    status: TenantUserStatus,
    kind: MemberKind,
  ): Promise<TenantMemberDto> {
    const res = await request<ApiResponse<TenantMemberDto>>(
      'PUT',
      `/api/saas-admin/tenants/${tenantId}/members/${membershipId}/status`,
      { json: { status }, query: { kind } },
    );
    return unwrap(res);
  },

  async remove(tenantId: number, membershipId: number, kind: MemberKind): Promise<void> {
    await request<void>(
      'DELETE',
      `/api/saas-admin/tenants/${tenantId}/members/${membershipId}`,
      { query: { kind } },
    );
  },

  // Spec 0058 (saas-auth SASATH-0076) — client-scoped OIDC access grant. Replaces
  // the removed global `…/users/{userId}/oidc-sso` toggle: access is now per
  // (member, client). `enabled=true` grants, `enabled=false` disables the grant
  // for THIS client. The caller refetches the effective-members read to reflect
  // the new `accessState`, so no response body is consumed here. Built to the
  // pending SASATH-0076 hand-off — see the delivery's Architect notes.
  async setOidcAccess(
    tenantId: number,
    userId: number,
    clientDbId: string,
    enabled: boolean,
  ): Promise<void> {
    await request<void>(
      'PUT',
      `/api/saas-admin/tenants/${tenantId}/users/${userId}/oidc-clients/${clientDbId}/access`,
      { json: { enabled } },
    );
  },
};
